import React, { useState, useEffect, useMemo, memo } from 'react';
import { Box } from '@mui/material';
import logo from 'app/images/IQ-Transparent.png';
import { useMiaChatV2 } from '../../../globalContexts/MiaChatContext';
import { StyledContainer, ContentContainer, PromptsSection, SectionTitle } from './StyledComponents';
import { filterPromptsBySearch, transformOperationsToPrompts } from './utils';
import { OperationsPanelProps, PromptSelection } from './types';
import HeaderSection from './components/HeaderSection';
import SearchSection from './components/SearchSection';
import CategoryFilter from './components/CategoryFilter';
import PromptsGrid from './components/PromptsGrid';
import LoadingOverlay from './components/LoadingOverlay';
import LoadingState from './components/LoadingState';
import ErrorState from './components/ErrorState';

const OperationsPanel: React.FC<OperationsPanelProps> = memo(({
  onPromptSelect,
  loading = false,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTag, setSelectedTag] = useState('All');
  
  const { 
    operations, 
    segments,
    loading: apiLoading, 
    error, 
    fetchOperations,
    getUniqueTags,
    getOperationsByTag,
    getSegmentsAsOperations
  } = useMiaChatV2();

  // Fetch operations when component mounts
  useEffect(() => {
    fetchOperations();
  }, []);

  // Memoize computed values to prevent recalculations
  const promptCategories = useMemo(() => getUniqueTags(), [getUniqueTags]);

  const segmentsAsOperations = useMemo(() => getSegmentsAsOperations(), [getSegmentsAsOperations]);

  const tagOperations = useMemo(() => getOperationsByTag(selectedTag), [getOperationsByTag, selectedTag]);

  const displayOperations = useMemo(() => {
    return segmentsAsOperations.length > 0 ? segmentsAsOperations : tagOperations;
  }, [segmentsAsOperations, tagOperations]);

  const filteredPrompts = useMemo(() => {
    return transformOperationsToPrompts(displayOperations, selectedTag);
  }, [displayOperations, selectedTag]);

  // Handle search submission
  const handleSearchSubmit = (query: string) => {
    onPromptSelect({title:query});
  };


  // Show loading state while API is loading
  if (apiLoading) {
    return (
      <StyledContainer>
        <ContentContainer>
          <LoadingState message="We're getting things ready for you..." />
        </ContentContainer>
      </StyledContainer>
    );
  }

  // Show error state if API fails
  if (error) {
    return (
      <StyledContainer>
        <ContentContainer>
          <ErrorState error={error} />
        </ContentContainer>
      </StyledContainer>
    );
  }

  return (
    <StyledContainer>
      <LoadingOverlay loading={loading} />
      
      <ContentContainer>
        <HeaderSection logo={logo} />

        <SearchSection
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          onSearchSubmit={handleSearchSubmit}
          loading={loading}
        />

        <PromptsSection>
          {/* <SectionTitle>
            {searchQuery ? `Search Results (${filteredPrompts.length})` : ''}
          </SectionTitle> */}

          {/* <CategoryFilter
            categories={promptCategories}
            selectedCategory={selectedTag}
            onCategorySelect={handleTagSelect}
            visible={!searchQuery}
          /> */}

          <PromptsGrid
            prompts={filteredPrompts}
            onPromptSelect={onPromptSelect}
            loading={loading}
          />
        </PromptsSection>
      </ContentContainer>
    </StyledContainer>
  );
});

// Add display name for debugging
OperationsPanel.displayName = 'OperationsPanel';

export default OperationsPanel;
